import { Component } from 'react';
import { InputFileInfo } from '../../../../types/file-upload.types';
import { FileUtils } from '../../../../utils/file-utils';
import { DateTimeUtils } from '../../../../ui-utils/date-time.utils';
import styles from './file-info-box.component.module.scss'
import { ConvertOptionsContext } from '../../../convert-options/convert-options-context';
import { untilDestroyed } from '../../../../utils/until-destroyed';
import { cloneDeep, includes, toInteger } from '../../../../utils/lodash-min';
import { Logger } from '../../../../models/logger';
export interface FileInfoBoxProps {
    fileInfo: InputFileInfo;
    convertOptionsContext: ConvertOptionsContext;
    fromType: string[];
    toType: string;
}

export interface FileInfoBoxState {
    outputFileSize: number;
}

export class FileInfoBoxComponent extends Component<FileInfoBoxProps> {
    private readonly logger = Logger.getLogger('FileInfoBoxComponent');

    readonly state: Readonly<FileInfoBoxState> = {
        outputFileSize: -1,
    };
    constructor(props) {
        super(props);
    }

    isConvertFromAndToVideo() {
        return FileUtils.isVideoFromType(this.props.fromType) && FileUtils.isVideoToType(this.props.toType);
    }

    isConvertFromAndToAudio() {
        return FileUtils.isAudioFromType(this.props.fromType) && FileUtils.isAudioToType(this.props.toType);
    }

    isConvertFromAndToImage() {
        return FileUtils.isImageFromType(this.props.fromType) && FileUtils.isImageToType(this.props.toType);
    }

    componentDidMount(): void {
        if (this.isConvertFromAndToVideo() || this.isConvertFromAndToImage() || this.isConvertFromAndToAudio()) {
            this.props.convertOptionsContext.onSelectedOptionsChanged
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: () => {
                        this.updateEstimatedOutputFileSize();
                    }
                });
            this.updateEstimatedOutputFileSize();
        }
    }

    updateEstimatedOutputFileSize() {
        try {
            this.setState({
                outputFileSize: -1,
            });
            if (this.isConvertFromAndToVideo()) {
                const currentCategories = this.props.convertOptionsContext.getConvertOptionCategories();
                const outputQuality = currentCategories.formCategories
                    .find(f => f.category === 'outputQuality');
                if (!outputQuality) {
                    return;
                }
                const outputQualityAndSizeOptions = outputQuality.formGroups.find(fg => fg.formGroupName === "outputQualityAndSize");
                const selectedOption = outputQualityAndSizeOptions.selections.find(option => option.selected);
                if (selectedOption.formName === "BY_PERCENTAGE") {
                    const percentageOption = selectedOption.formChildren.find(option => option.formName === "percentage");
                    const selectedPercentageOption = percentageOption.selections.find(option => option.selected);
                    const percentage = parseInt(selectedPercentageOption.formValue);
                    this.setState({
                        outputFileSize: this.props.fileInfo.size * (percentage / 100)
                    });
                }
            }
            if (this.isConvertFromAndToAudio()) {
                const currentCategories = this.props.convertOptionsContext.getConvertOptionCategories();
                const outputQuality = currentCategories.formCategories
                    .find(f => f.category === 'outputQuality');
                if (!outputQuality) {
                    return;
                }
                const outputQualityAndSizeOptions = outputQuality.formGroups.find(fg => fg.formGroupName === "outputQualityAndSize");
                const selectedOption = outputQualityAndSizeOptions.selections.find(option => option.selected);
                if (selectedOption.formName === "BY_SIZE_PERCENTAGE") {
                    const percentageOption = selectedOption.formChildren.find(option => option.formName === "sizePercentage");
                    const selectedPercentageOption = percentageOption.selections.find(option => option.selected);
                    const percentage = parseInt(selectedPercentageOption.formValue);
                    this.setState({
                        outputFileSize: this.props.fileInfo.size * (percentage / 100)
                    });
                } else if (selectedOption.formName === "BY_TARGET_SIZE") {
                    const targetSizeOption = selectedOption.formChildren[0];
                    const selectedSizeValue = targetSizeOption.formValue.input;
                    const selectedSizeUnit = targetSizeOption.formValue.dropdown.formValue;
                    let targetSize = parseFloat(selectedSizeValue);
                    if (selectedSizeUnit === "MB") {
                        targetSize = targetSize * 1000 * 1000;
                    }
                    if (selectedSizeUnit === "kB") {
                        targetSize = targetSize * 1000;
                    }
                    this.setState({
                        outputFileSize: toInteger(targetSize),
                    });
                }
            }
            if (this.isConvertFromAndToImage()) {
                const currentCategories = this.props.convertOptionsContext.getConvertOptionCategories();
                const outputQuality = currentCategories.formCategories
                    .find(f => f.category === 'outputQualityOrTargetSize');
                if (!outputQuality) {
                    return;
                }
                const outputQualityAndSizeOptions = outputQuality.formGroups.find(fg => fg.formGroupName === 'outputQuality');
                const selectedOption = outputQualityAndSizeOptions.selections.find(option => option.selected);
                if (selectedOption.formName === "BY_SIZE_PERCENTAGE") {
                    const percentageOption = selectedOption.formChildren.find(option => option.formName === "sizePercentage");
                    const selectedPercentageOption = percentageOption.selections.find(option => option.selected);
                    const percentage = parseInt(selectedPercentageOption.formValue);
                    this.setState({
                        outputFileSize: this.props.fileInfo.size * (percentage / 100)
                    });
                } else if (selectedOption.formName === "BY_TARGET_SIZE") {
                    const targetSizeOption = selectedOption.formChildren[0];
                    const selectedSizeValue = targetSizeOption.formValue.input;
                    const selectedSizeUnit = targetSizeOption.formValue.dropdown.formValue;
                    let targetSize = parseFloat(selectedSizeValue);
                    if (selectedSizeUnit === "MB") {
                        targetSize = targetSize * 1000 * 1000;
                    }
                    if (selectedSizeUnit === "kB") {
                        targetSize = targetSize * 1000;
                    }
                    this.setState({
                        outputFileSize: toInteger(targetSize),
                    });
                }

            }
        } catch (e) {
            this.logger.error({
                message: "Exception thrown while calculating estimated file size",
                data: this.props.fileInfo,
                error: e,
            });
        }
    }

    createBadge(key, value, className="badge text-bg-secondary") {
        return <span className={className}>{key}: {value}</span>
    }

    render() {
        const shouldDisplayWidth = this.props.fileInfo.width && this.props.fileInfo.width > -1;
        const shouldDisplayHeight = this.props.fileInfo.height && this.props.fileInfo.height > -1;
        const shouldDisplayWidthAndHeight = shouldDisplayWidth && shouldDisplayHeight;
        return <div className={styles['file-info']}>
            {
                this.props.fileInfo.duration && this.props.fileInfo.duration > -1 && 
                    this.createBadge('Duration', DateTimeUtils.secondsToTime(this.props.fileInfo.duration, true)) || ''
            }
            {
                shouldDisplayWidthAndHeight && 
                this.createBadge('WxH', `${this.props.fileInfo.width}x${this.props.fileInfo.height}px`) || ''
            }
            {
                this.props.fileInfo.video_bitrate && this.props.fileInfo.video_bitrate > -1 &&
                this.createBadge('Video Bitrate', FileUtils.toReadableBits(this.props.fileInfo.video_bitrate, 'rate')) || ''
            }
            {
                this.props.fileInfo.audio_bitrate && this.props.fileInfo.audio_bitrate > -1 &&
                this.createBadge('Audio Bitrate', FileUtils.toReadableBits(this.props.fileInfo.audio_bitrate, 'rate')) || ''
            }
            {
                this.props.fileInfo.numberOfPages && this.props.fileInfo.numberOfPages > -1 &&
                this.createBadge('Page Count', this.props.fileInfo.numberOfPages) || ''
            }
            {
                this.props.fileInfo.size && this.props.fileInfo.size > -1 && 
                this.createBadge('Size', FileUtils.toReadableBytes(this.props.fileInfo.size, 'quantity')) || ''
            }
            {
                this.state.outputFileSize && this.state.outputFileSize > -1 && 
                this.createBadge('Compressed Size', FileUtils.toReadableBytes(this.state.outputFileSize, 'quantity'), "badge text-bg-info") || ''
            }
        </div>
    }
}